/**
 * SplitRuleList — Story 2.3-UI (Split-Editor, Referenz splits.html)
 *
 * Regel-Liste je Host/Hotspot: Identity (Host + Hotspot), Regel-Text
 * (split_type + basis), Auszahlungswaehrung und Status-Pill. Der Status
 * wird aus start_date/end_date abgeleitet (der Contract kennt kein
 * eigenes Status-Feld).
 *
 * WP-F: Tabs "Aktiv" | "Abgelaufen". Der Listen-Endpunkt
 * (GET /api/v2/host_split_rules) kennt keinen Status-/Zeitfenster-Filter
 * (nur page/per_page/hotspot_id/host_id, siehe api-types) — abgelaufene
 * Regeln werden daher clientseitig aus derselben bereits geladenen Liste
 * herausgefiltert (splitRuleStatus === 'ended'), nicht separat nachgeladen.
 */
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { splitRuleStatus, type SplitRuleStatus } from './splitRuleHelpers'
import { useRuleText } from './useRuleText'
import { useHosts, useSplitRules, type HostSplitRule } from './useSplitRules'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'
import { formatLocalDate } from '../../lib/locale'
import { PremiumRequired } from '../../components/PremiumRequired'
import { isPremiumRequiredError } from '../../lib/premiumError'

type ListTab = 'active' | 'expired'

const STATUS_ROW_CLASS: Record<SplitRuleStatus, string> = {
  active: 'is-online',
  scheduled: 'is-warn',
  ended: 'is-offline',
}

const STATUS_PILL_CLASS: Record<SplitRuleStatus, string> = {
  active: 'pill-success',
  scheduled: 'pill-warning',
  ended: 'pill-danger',
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return parts
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export function SplitRuleList() {
  const { t } = useTranslation('splits')
  const rulesQuery = useSplitRules()
  const hostsQuery = useHosts()
  const ruleText = useRuleText()
  const [activeTab, setActiveTab] = useState<ListTab>('active')

  const hostName = (id: number | undefined): string => {
    const host = hostsQuery.data?.find((h) => h.id === id)
    return host?.name ?? t('list.hostFallback', { id: id ?? '?' })
  }
  // Issue #52: der Hotspot-Name kommt jetzt direkt aus der Regel
  // (hotspot_name, Contract) statt ueber die owned+active-gefilterte
  // Hotspot-Liste nachgeschlagen zu werden — sonst fehlten Namen fuer
  // inaktive/nicht mehr eigene Hotspots ("Hotspot #2").
  const hotspotName = (rule: HostSplitRule): string => {
    const fallback = t('list.hotspotFallback', { id: rule.hotspot_id ?? '?' })
    return hotspotDisplayName(rule.hotspot_name ?? null, null, fallback)
  }

  if (rulesQuery.isLoading) {
    return (
      <div className="list" aria-label={t('list.ariaLabel')} data-testid="splits-list-loading">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="card skeleton" style={{ height: 68 }} />
        ))}
      </div>
    )
  }

  if (rulesQuery.isError) {
    // Review-Fix (premium-gates): /api/v2/host_split_rules ist seit B1
    // 402-gegated. SplitsLayout gated bereits proaktiv ueber den Account,
    // aber bei einem veralteten/nicht mehr passenden Premium-Status (z. B.
    // Premium laeuft waehrend der Session ab) faengt dieser reaktive Zweig
    // den Fall ab — kein roter "Regeln konnten nicht geladen werden", sondern
    // derselbe Premium-Hinweis wie im Layout.
    if (isPremiumRequiredError(rulesQuery.error)) {
      return <PremiumRequired data-testid="splits-premium-required" />
    }
    return (
      <div className="card card-pad" data-testid="splits-list-error">
        <p className="alert-error" role="alert">
          {t('list.error')}
        </p>
      </div>
    )
  }

  const rules = rulesQuery.data ?? []

  if (rules.length === 0) {
    return (
      <div className="card card-pad" data-testid="splits-list-empty">
        <p className="muted" style={{ marginBottom: 12 }}>
          {t('list.empty')}
        </p>
        <Link to="/splits/create" className="btn btn-primary">
          {t('list.emptyCta')}
        </Link>
      </div>
    )
  }

  // WP-F: "Abgelaufen" = Regeln mit end_date in der Vergangenheit
  // (splitRuleStatus === 'ended'); "Aktiv" zeigt alles andere (aktiv +
  // geplant), also unveraendert die bisherige Liste inkl. Status-Pill.
  const activeRules = rules.filter((rule) => splitRuleStatus(rule) !== 'ended')
  const expiredRules = rules.filter((rule) => splitRuleStatus(rule) === 'ended')

  return (
    <div data-testid="splits-list">
      <div className="tabs" role="tablist" aria-label={t('list.tabsAriaLabel')}>
        <button
          type="button"
          role="tab"
          id="splits-tab-active"
          aria-selected={activeTab === 'active'}
          aria-controls="splits-panel-active"
          className={`tab${activeTab === 'active' ? ' is-active' : ''}`}
          data-testid="splits-tab-active"
          onClick={() => setActiveTab('active')}
        >
          {t('list.tabActive')}
        </button>
        <button
          type="button"
          role="tab"
          id="splits-tab-expired"
          aria-selected={activeTab === 'expired'}
          aria-controls="splits-panel-expired"
          className={`tab${activeTab === 'expired' ? ' is-active' : ''}`}
          data-testid="splits-tab-expired"
          onClick={() => setActiveTab('expired')}
        >
          {t('list.tabExpired')}
        </button>
      </div>

      {activeTab === 'active' ? (
        <div
          role="tabpanel"
          id="splits-panel-active"
          aria-labelledby="splits-tab-active"
          className="list"
          aria-label={t('list.ariaLabel')}
          style={{ marginTop: 12 }}
        >
          {activeRules.length === 0 ? (
            <div className="card card-pad">
              <p className="muted" style={{ margin: 0 }}>
                {t('list.activeEmpty')}
              </p>
            </div>
          ) : (
            <>
              <div className="list-head splits-row" role="presentation">
                <span>{t('list.hostHeader')}</span>
                <span>{t('list.ruleHeader')}</span>
                <span className="splits-col-hotspots">{t('list.hotspotsHeader')}</span>
                <span className="splits-col-status">{t('list.statusHeader')}</span>
              </div>
              {activeRules.map((rule, i) => {
                const status = splitRuleStatus(rule)
                const host = hostName(rule.host_id)
                return (
                  <div
                    key={rule.id ?? i}
                    className={`list-row splits-row ${STATUS_ROW_CLASS[status]}`}
                    data-testid="splits-list-row"
                  >
                    <div className="identity">
                      <div className={`identity-img tone-${(i % 8) + 1}`}>
                        <span>{initials(host)}</span>
                      </div>
                      <div className="identity-meta">
                        <div className="identity-title">{host}</div>
                        <div className="identity-sub">{hotspotName(rule)}</div>
                      </div>
                    </div>
                    <div>
                      <div className="splits-col-label">{t('list.ruleLabel')}</div>
                      <div className="num-sm splits-col-value">{ruleText(rule)}</div>
                    </div>
                    <div className="splits-col-hotspots">
                      <div className="splits-col-label">{t('list.hotspotsHeader')}</div>
                      <div className="num-sm splits-col-value">
                        {t('list.hotspotCount', { count: rule.hotspot_id != null ? 1 : 0 })}
                      </div>
                    </div>
                    <div className="splits-col-status row" style={{ gap: 6 }}>
                      <span className={`pill ${STATUS_PILL_CLASS[status]}`}>
                        {t(`status.${status}`)}
                      </span>
                      <Link
                        to="/splits/$id"
                        params={{ id: String(rule.id) }}
                        className="btn btn-sm"
                        data-testid={`splits-edit-${rule.id}`}
                      >
                        {t('list.edit')}
                      </Link>
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>
      ) : (
        <div
          role="tabpanel"
          id="splits-panel-expired"
          aria-labelledby="splits-tab-expired"
          className="list"
          aria-label={t('list.tabExpired')}
          style={{ marginTop: 12 }}
          data-testid="splits-list-expired"
        >
          <p className="muted" style={{ fontSize: 12, margin: '0 0 12px' }}>
            {t('list.expiredHint')}
          </p>
          {expiredRules.length === 0 ? (
            <div className="card card-pad">
              <p className="muted" style={{ margin: 0 }}>
                {t('list.expiredEmpty')}
              </p>
            </div>
          ) : (
            expiredRules.map((rule, i) => {
              const host = hostName(rule.host_id)
              return (
                <div
                  key={rule.id ?? i}
                  className="list-row splits-row splits-row-ended"
                  data-testid="splits-list-row-expired"
                  style={{ opacity: 0.65 }}
                >
                  <div className="identity">
                    <div className={`identity-img tone-${(i % 8) + 1}`}>
                      <span>{initials(host)}</span>
                    </div>
                    <div className="identity-meta">
                      <div className="identity-title">{host}</div>
                      <div className="identity-sub">{hotspotName(rule)}</div>
                    </div>
                  </div>
                  <div>
                    <div className="splits-col-label">{t('list.ruleLabel')}</div>
                    <div className="num-sm splits-col-value">{ruleText(rule)}</div>
                  </div>
                  <div className="splits-col-hotspots">
                    <div className="splits-col-label">{t('list.hotspotsHeader')}</div>
                    <div className="num-sm splits-col-value">
                      {t('list.hotspotCount', { count: rule.hotspot_id != null ? 1 : 0 })}
                    </div>
                  </div>
                  <div className="splits-col-status">
                    <span className="muted" style={{ fontSize: 12 }}>
                      {t('list.endedOn', { date: formatLocalDate(rule.end_date) })}
                    </span>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
